import { Observable } from '../../tools/observable';
import { createValueHistory } from '../../tools/valueHistory';
import { clocksOrigin, dateNow, elapsed, ONE_HOUR, ONE_MINUTE, ONE_SECOND, relativeNow, timeStampNow, } from '../../tools/utils/timeUtils';
import { addEventListener, addEventListeners } from '../../browser/addEventListener';
import { clearInterval, clearTimeout, setInterval, setTimeout } from '../../tools/timer';
import { mockable } from '../../tools/mockable';
import { noop, throttle } from '../../tools/utils/functionUtils';
import { generateUUID } from '../../tools/utils/stringUtils';
import { isWorkerEnvironment } from '../../tools/globalObject';
import { display } from '../../tools/display';
import { isSampled } from '../sampler';
import { addTelemetryMetrics } from '../telemetry';
import { monitorError } from '../../tools/monitor';
import { getCookies } from '../../browser/cookie';
import { ExperimentalFeature, isExperimentalFeatureEnabled } from '../../tools/experimentalFeatures';
import { SESSION_STORE_KEY } from './storeStrategies/sessionStoreStrategy';
import { SESSION_TIME_OUT_DELAY } from './sessionConstants';
import { expandOnly, expandOrRenew, getCreatedDate, getExpireDate, getExpiredSessionState, initializeSession, isSessionInExpiredState, } from './sessionState';
import { getSessionStoreStrategy } from './sessionStore';
export const VISIBILITY_CHECK_DELAY = ONE_MINUTE;
const SESSION_CONTEXT_TIMEOUT_DELAY = SESSION_TIME_OUT_DELAY;
// Maximum duration for which we can send data related to a session.
//
// The backend behavior depends on how old the session is when it receives an event:
// - Session started < 4h ago: the backend updates the session normally.
// - Session started between 4h and 24h ago: the backend ignores the event (safe).
// - Session started > 24h ago: the backend recreates a session with that id (problematic).
//
// We choose 12h as a threshold — safely between 4h and 24h — to avoid both recreating
// sessions and discarding too many legitimate late events.
export const TRACKED_SESSION_MAX_AGE = ONE_HOUR * 12;
let stopCallbacks = [];
export async function startSessionManager(configuration, trackingConsentState) {
    const startTime = relativeNow();
    const renewObservable = new Observable();
    const expireObservable = new Observable();
    let expireContext;
    if (!configuration.sessionStoreStrategyType) {
        display.warn('No storage available for session. We will not send any data.');
        return;
    }
    const strategy = mockable(getSessionStoreStrategy)(configuration.sessionStoreStrategyType, configuration);
    const sessionContextHistory = createValueHistory({
        expireDelay: SESSION_CONTEXT_TIMEOUT_DELAY,
    });
    stopCallbacks.push(() => sessionContextHistory.stop());
    let sessionExpired = false;
    const { throttled: throttledExpandOrRenew, cancel: cancelExpandOrRenew } = throttle(() => {
        sessionExpired = false;
        strategy.setSessionState((state) => expandOrRenew(state, configuration)).catch(monitorError);
    }, ONE_SECOND);
    stopCallbacks.push(cancelExpandOrRenew);
    let expirationTimeoutId;
    stopCallbacks.push(() => clearTimeout(expirationTimeoutId));
    let stopped = false;
    stopCallbacks.push(() => {
        stopped = true;
    });
    const initialState = await resolveInitialState().catch(monitorError);
    if (!initialState || stopped) {
        return;
    }
    // Consent is always granted when the session manager is started, but it may
    // be revoked during the async initialization (e.g., while waiting for cookie lock).
    if (!trackingConsentState.isGranted()) {
        expire();
        return;
    }
    sessionContextHistory.add(buildSessionContext(initialState), clocksOrigin().relative);
    scheduleExpirationTimeout(initialState);
    subscribeToSessionChanges();
    setupSessionTracking();
    // monitor-until: 2026-10-15
    addTelemetryMetrics("Session manager init metrics" /* TelemetryMetrics.SESSION_MANAGER_INIT_METRICS_TELEMETRY_NAME */, {
        metrics: { duration: elapsed(startTime, relativeNow()) },
    });
    return buildSessionManager();
    async function resolveInitialState() {
        let state = {};
        await strategy.setSessionState((currentState) => {
            const initialState = initializeSession(currentState, configuration);
            state = expandOrRenew(initialState, configuration);
            return state;
        });
        return state;
    }
    function subscribeToSessionChanges() {
        const subscription = strategy.sessionObservable.subscribe(({ cookieValues, sessionState }) => {
            scheduleExpirationTimeout(sessionState);
            handleStateChange(sessionState, { from: 'sessionObservable', cookieValues });
        });
        stopCallbacks.push(() => subscription.unsubscribe());
    }
    function scheduleExpirationTimeout(state) {
        clearTimeout(expirationTimeoutId);
        const expireDate = getExpireDate(state);
        if (expireDate) {
            const delay = expireDate - dateNow();
            expirationTimeoutId = setTimeout(() => {
                strategy
                    .setSessionState((state) => {
                    if (isSessionInExpiredState(state)) {
                        if (!trackingConsentState.isGranted()) {
                            delete state.anonymousId;
                        }
                        return getExpiredSessionState(state, configuration);
                    }
                    return state;
                })
                    .catch(monitorError);
            }, delay);
        }
    }
    function setupSessionTracking() {
        trackingConsentState.observable.subscribe(() => {
            if (trackingConsentState.isGranted()) {
                sessionExpired = false;
                strategy.setSessionState((state) => expandOrRenew(state, configuration)).catch(monitorError);
            }
            else {
                expire();
            }
        });
        if (!isWorkerEnvironment) {
            trackActivity(configuration, () => {
                if (trackingConsentState.isGranted()) {
                    throttledExpandOrRenew();
                }
            });
            trackVisibility(configuration, () => {
                if (!sessionExpired) {
                    strategy.setSessionState((state) => expandOnly(state)).catch(monitorError);
                }
            });
            trackResume(configuration, () => {
                strategy.setSessionState((state) => initializeSession(state, configuration)).catch(monitorError);
            });
        }
    }
    function buildSessionManager() {
        return {
            findSession: (startTime, options) => sessionContextHistory.find(startTime, options),
            findTrackedSession: (startTime, options) => {
                const session = sessionContextHistory.find(startTime, options);
                if (!session || session.id === 'invalid' || !isSampled(session.id, configuration.sessionSampleRate)) {
                    return;
                }
                if (dateNow() - session.createdAt > TRACKED_SESSION_MAX_AGE) {
                    return;
                }
                return session;
            },
            renewObservable,
            expireObservable,
            expire,
            updateSessionState: (partialState) => {
                strategy.setSessionState((state) => ({ ...state, ...partialState })).catch(monitorError);
            },
        };
    }
    function handleStateChange(newState, { from, cookieValues }) {
        const previousSession = sessionContextHistory.find();
        const hadSession = previousSession?.id !== undefined;
        const hasSession = newState.id !== undefined;
        const sessionIdChanged = hadSession && hasSession && previousSession.id !== newState.id;
        if (hadSession && (!hasSession || sessionIdChanged)) {
            // Session expired or replaced
            if (isExperimentalFeatureEnabled(ExperimentalFeature.SESSION_RENEWAL_DEBUG_CONTEXT)) {
                expireContext = {
                    previousSession: previousSession && { ...previousSession },
                    newState: { ...newState },
                    from,
                    cookieValues,
                    cookies: getCookies(SESSION_STORE_KEY),
                    locksAvailable: Boolean(globalThis.navigator?.locks),
                    cookieStoreAvailable: Boolean(globalThis.cookieStore),
                };
            }
            sessionExpired = true;
            expireObservable.notify();
            sessionContextHistory.closeActive(relativeNow());
        }
        if (hasSession && (!hadSession || sessionIdChanged)) {
            if (sessionExpired) {
                // Don't adopt another tab's session — this tab needs its own user interaction to renew
                return;
            }
            // New session appeared
            sessionContextHistory.add(buildSessionContext(newState), relativeNow());
            renewObservable.notify(isExperimentalFeatureEnabled(ExperimentalFeature.SESSION_RENEWAL_DEBUG_CONTEXT)
                ? {
                    expire: expireContext,
                    renew: {
                        previousSession: previousSession && { ...previousSession },
                        newState: { ...newState },
                        from,
                        cookieValues,
                        cookies: getCookies(SESSION_STORE_KEY),
                        locksAvailable: Boolean(globalThis.navigator?.locks),
                        cookieStoreAvailable: Boolean(globalThis.cookieStore),
                    },
                }
                : undefined);
        }
        else if (hadSession && hasSession && !sessionIdChanged) {
            // Same session,
            // Mutate the session context in the history for replay forced changes
            previousSession.isReplayForced = !!newState.forcedReplay;
        }
    }
    function expire() {
        cancelExpandOrRenew();
        // Update in-memory state synchronously so events stop being collected immediately
        const expiredState = getExpiredSessionState(sessionContextHistory.find(), configuration);
        if (!trackingConsentState.isGranted()) {
            delete expiredState.anonymousId;
        }
        handleStateChange(expiredState, { from: 'expire' });
        // Persist to storage asynchronously
        strategy
            .setSessionState((state) => {
            if (!trackingConsentState.isGranted()) {
                delete state.anonymousId;
            }
            return getExpiredSessionState(state, configuration);
        })
            .catch(monitorError);
    }
    function buildSessionContext(sessionState) {
        const createdAt = getCreatedDate(sessionState) ?? timeStampNow();
        if (!sessionState.id) {
            return {
                id: 'invalid',
                isReplayForced: false,
                anonymousId: undefined,
                createdAt,
            };
        }
        return {
            id: sessionState.id,
            isReplayForced: !!sessionState.forcedReplay,
            anonymousId: sessionState.anonymousId,
            createdAt,
        };
    }
}
export function startSessionManagerStub() {
    const stubSessionId = generateUUID();
    let sessionContext = {
        id: stubSessionId,
        isReplayForced: false,
        anonymousId: undefined,
        createdAt: timeStampNow(),
    };
    return Promise.resolve({
        findSession: () => sessionContext,
        findTrackedSession: () => sessionContext,
        renewObservable: new Observable(),
        expireObservable: new Observable(),
        expire: noop,
        updateSessionState: (state) => {
            sessionContext = {
                ...sessionContext,
                ...state,
            };
        },
    });
}
export function stopSessionManager() {
    stopCallbacks.forEach((e) => e());
    stopCallbacks = [];
}
function trackActivity(configuration, expandOrRenewSession) {
    const { stop } = addEventListeners(configuration, window, ["click" /* DOM_EVENT.CLICK */, "touchstart" /* DOM_EVENT.TOUCH_START */, "keydown" /* DOM_EVENT.KEY_DOWN */, "scroll" /* DOM_EVENT.SCROLL */], expandOrRenewSession, { capture: true, passive: true });
    stopCallbacks.push(stop);
}
function trackVisibility(configuration, expandSession) {
    const expandSessionWhenVisible = () => {
        if (document.visibilityState === 'visible') {
            expandSession();
        }
    };
    const { stop } = addEventListener(configuration, document, "visibilitychange" /* DOM_EVENT.VISIBILITY_CHANGE */, expandSessionWhenVisible);
    stopCallbacks.push(stop);
    const visibilityCheckInterval = setInterval(expandSessionWhenVisible, VISIBILITY_CHECK_DELAY);
    stopCallbacks.push(() => {
        clearInterval(visibilityCheckInterval);
    });
}
function trackResume(configuration, cb) {
    const { stop } = addEventListener(configuration, window, "resume" /* DOM_EVENT.RESUME */, cb, { capture: true });
    stopCallbacks.push(stop);
}
//# sourceMappingURL=sessionManager.js.map